// Meta Pixel Utility
// Pixel ID: 1097761255594088

declare global {
  interface Window {
    fbq: any
    _fbq: any
  }
}

export const META_PIXEL_ID = '1097761255594088'

// ============================================
// TRACKING STATE (prevents duplicate events)
// ============================================
const trackingState = {
  scrollMilestones: { 25: false, 50: false, 75: false },
  engagementMilestones: { 15: false, 30: false },
  initialized: false,
  lastJoinBackstageTime: 0, // Anti-duplicate for rapid clicks
}

// ============================================
// EVENT ID GENERATOR (for CAPI deduplication)
// ============================================

/**
 * Generate unique event ID for Browser/Server deduplication
 * Format: timestamp_randomString
 */
function generateEventId(): string {
  const timestamp = Date.now()
  const random = Math.random().toString(36).substring(2, 10)
  return `${timestamp}_${random}`
}

// ============================================
// ADVANCED MATCHING READINESS
// Infrastructure for future hashed data capture
// ============================================
interface UserData {
  em?: string // hashed email
  ph?: string // hashed phone
  external_id?: string
  client_ip_address?: string
  client_user_agent?: string
}

let advancedMatchingData: UserData = {}

/**
 * Set advanced matching data (for future use)
 * Data should be hashed before passing to this function
 */
export function setAdvancedMatchingData(data: Partial<UserData>) {
  advancedMatchingData = { ...advancedMatchingData, ...data }

  // Capture user agent automatically
  if (
    typeof window !== 'undefined' &&
    !advancedMatchingData.client_user_agent
  ) {
    advancedMatchingData.client_user_agent = navigator.userAgent
  }
}

/**
 * Get current advanced matching data
 */
export function getAdvancedMatchingData(): UserData {
  return { ...advancedMatchingData }
}

// ============================================
// CORE PIXEL INITIALIZATION
// ============================================

/**
 * Initialize Meta Pixel
 * This should be called once when the app loads
 */
export function initMetaPixel() {
  if (typeof window === 'undefined') return

  // Prevent duplicate initialization
  if (trackingState.initialized) return
  trackingState.initialized = true

  // Check if fbq is already loaded
  if (window.fbq) return

  // Initialize fbq
  const fbq = function (...args: any[]) {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, args)
    } else {
      fbq.queue.push(args)
    }
  }

  if (!window.fbq) {
    window.fbq = fbq
  }

  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []

  // Load Meta Pixel script (async - no render blocking)
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)

  // Initialize pixel with ID
  window.fbq('init', META_PIXEL_ID)

  // Track automatic PageView with event_id for CAPI deduplication
  window.fbq('track', 'PageView', {}, { eventID: generateEventId() })

  // Capture user agent for advanced matching readiness
  setAdvancedMatchingData({
    client_user_agent: navigator.userAgent,
  })
}

// ============================================
// CONVERSION TRACKING
// ============================================

/**
 * Track custom event: Join_Backstage
 * Enhanced with parameters for better data quality
 * Includes event_id for CAPI deduplication
 * This should be called when user clicks the main CTA to Backstage
 *
 * @returns boolean - true if event was fired, false if blocked (duplicate)
 */
export function trackJoinBackstage(): boolean {
  if (typeof window === 'undefined' || !window.fbq) {
    console.warn('Meta Pixel not initialized')
    return false
  }

  // Anti-duplicate: Block rapid clicks within 1 second
  const now = Date.now()
  if (now - trackingState.lastJoinBackstageTime < 1000) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Meta Pixel: Join_Backstage blocked (duplicate click)')
    }
    return false
  }
  trackingState.lastJoinBackstageTime = now

  // Generate unique event_id for CAPI deduplication
  const eventId = generateEventId()

  // Enhanced event parameters for better attribution
  const eventParams = {
    content_name: 'Alexa Backstage Entry',
    content_category: 'Creator Funnel',
    value: 1,
    currency: 'USD',
  }

  // Fire custom event with enhanced parameters and event_id
  window.fbq('trackCustom', 'Join_Backstage', eventParams, { eventID: eventId })

  // Optional: Log for debugging (remove in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('Meta Pixel: Join_Backstage event fired', {
      eventParams,
      eventId,
    })
  }

  return true
}

/**
 * Check if URL is a Backstage link
 */
export function isBackstageLink(url: string): boolean {
  return url.includes('backstage.cam')
}

// ============================================
// ENGAGEMENT TRACKING
// ============================================

/**
 * Track scroll depth milestones
 * Fires: Scroll_25, Scroll_50, Scroll_75
 */
export function trackScrollDepth(percentage: number) {
  if (typeof window === 'undefined' || !window.fbq) return

  const milestones = [25, 50, 75] as const

  for (const milestone of milestones) {
    if (percentage >= milestone && !trackingState.scrollMilestones[milestone]) {
      trackingState.scrollMilestones[milestone] = true

      const eventId = generateEventId()
      window.fbq(
        'trackCustom',
        `Scroll_${milestone}`,
        {
          scroll_depth: milestone,
          page_location: window.location.href,
        },
        { eventID: eventId },
      )

      if (process.env.NODE_ENV === 'development') {
        console.log(`Meta Pixel: Scroll_${milestone} event fired`, { eventId })
      }
    }
  }
}

/**
 * Track time on page engagement
 * Fires: Engaged_15s, Engaged_30s
 */
export function trackEngagementTime(seconds: number) {
  if (typeof window === 'undefined' || !window.fbq) return

  const milestones = [15, 30] as const

  for (const milestone of milestones) {
    if (
      seconds >= milestone &&
      !trackingState.engagementMilestones[milestone]
    ) {
      trackingState.engagementMilestones[milestone] = true

      const eventId = generateEventId()
      window.fbq(
        'trackCustom',
        `Engaged_${milestone}s`,
        {
          engagement_time: milestone,
          page_location: window.location.href,
        },
        { eventID: eventId },
      )

      if (process.env.NODE_ENV === 'development') {
        console.log(`Meta Pixel: Engaged_${milestone}s event fired`, {
          eventId,
        })
      }
    }
  }
}

/**
 * Track CTA hover intent (hover > 1.5s)
 * Fires: CTA_Hover (once per session)
 */
let ctaHoverTracked = false
export function trackCTAHover(): boolean {
  if (typeof window === 'undefined' || !window.fbq) return false

  // Only track once per session
  if (ctaHoverTracked) return false
  ctaHoverTracked = true

  const eventId = generateEventId()
  window.fbq(
    'trackCustom',
    'CTA_Hover',
    {
      content_name: 'Alexa Backstage Entry',
      intent_signal: 'hover_duration',
    },
    { eventID: eventId },
  )

  if (process.env.NODE_ENV === 'development') {
    console.log('Meta Pixel: CTA_Hover event fired', { eventId })
  }

  return true
}

// ============================================
// ENGAGEMENT TRACKING INITIALIZER
// ============================================

/**
 * Initialize all engagement tracking listeners
 * Call this once after pixel initialization
 */
export function initEngagementTracking() {
  if (typeof window === 'undefined') return

  // Scroll tracking
  let ticking = false
  const handleScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        const scrollHeight =
          document.documentElement.scrollHeight - window.innerHeight
        const scrollPercentage =
          scrollHeight > 0 ? (window.scrollY / scrollHeight) * 100 : 0
        trackScrollDepth(scrollPercentage)
        ticking = false
      })
      ticking = true
    }
  }
  window.addEventListener('scroll', handleScroll, { passive: true })

  // Time on page tracking
  const startTime = Date.now()
  const checkEngagement = () => {
    const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000)
    trackEngagementTime(elapsedSeconds)
  }

  // Check engagement at intervals
  const engagementInterval = setInterval(checkEngagement, 1000)

  // Stop tracking when page is hidden
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearInterval(engagementInterval)
    }
  })

  // Cleanup on page unload (optional)
  window.addEventListener('beforeunload', () => {
    clearInterval(engagementInterval)
  })
}
